import type {
  SlotAssetGenerationRequest,
  SlotAssetResult,
  SlotComponentDef,
} from '../../../core/types/slot-asset-generator.types';
import { GOLDEN_PROMPT_SUFFIX } from '../../../core/types/slot-asset-generator.types';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import type { ImageGenerationSettings, ReferenceImage } from '../../../core/types/image-generation.types';
import { generateImages } from '../image-generation/ImageGeneratorService';
import {
  writeFileAsArrayBuffer,
  readFileAsArrayBuffer,
  createDirectory,
  listDirectory,
  hasProjectRoot,
} from '../FileSystemService';

const generateId = () => Math.random().toString(36).substring(2, 11);

/**
 * Resolve a project-relative path to a blob URL for use by the image generation API.
 * Returns the original string if it's already a blob/http URL.
 */
async function resolveRefToBlobUrl(ref: string): Promise<string> {
  if (ref.startsWith('blob:') || ref.startsWith('http:') || ref.startsWith('https:') || ref.startsWith('data:')) {
    return ref;
  }
  // Treat as project-relative path
  const buffer = await readFileAsArrayBuffer(ref);
  const ext = ref.split('.').pop()?.toLowerCase() || 'png';
  const mimeMap: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' };
  const blob = new Blob([buffer], { type: mimeMap[ext] || 'image/png' });
  return URL.createObjectURL(blob);
}

const STAGGER_MS = 1500;

/**
 * Structured prompt output: system context (shown in UI) + user prompt + full combined prompt.
 */
export interface StructuredSlotPrompt {
  systemContext: string;   // Everything except the user prompt (shown in UI)
  userPrompt: string;      // The user's original text
  fullPrompt: string;      // systemContext + "\n\nGenerate: " + userPrompt
}

/**
 * Build a structured slot prompt with Role/Task/Guidelines framing.
 * The systemContext is displayed verbatim in the UI, and fullPrompt is sent to the API.
 */
export function buildStructuredSlotPrompt(request: SlotAssetGenerationRequest): StructuredSlotPrompt {
  const { component, style, quality, prompt } = request;

  // If the user manually edited the system context, use their version directly
  if (request.systemContextOverride) {
    const fullPrompt = `${request.systemContextOverride}\n\nGenerate: ${prompt}`;
    return { systemContext: request.systemContextOverride, userPrompt: prompt, fullPrompt };
  }

  const lines: string[] = [];

  lines.push('Role: You are an expert 3D UI designer creating a cohesive set of high-quality game/app assets.');
  lines.push(`Task: Generate a ${component.name} asset adhering to the following strict visual rules to ensure absolute consistency.`);
  lines.push('');
  lines.push('Mandatory Visual Guidelines:');

  // Dimensions + format
  const w = component.width ?? 512;
  const h = component.height ?? 512;
  const formatLabel = component.format === 'png_transparent' ? 'PNG with alpha'
    : component.format === 'jpg' ? 'JPEG' : 'PNG';
  lines.push(`- Output: ${w}\u00d7${h} pixels, ${formatLabel}`);

  // Style
  const styleText = resolveStyleText(style);
  if (styleText) lines.push(`- Style: ${styleText}`);

  // Quality
  const qualityText = resolveQualityText(quality);
  if (qualityText) lines.push(`- Quality: ${qualityText}`);

  // Background
  if (component.format === 'png_transparent') {
    lines.push('- Background: transparent, no background, isolated subject, PNG with alpha channel');
  }

  // Component notes
  if (component.notes) lines.push(`- Context: ${component.notes}`);

  // Golden variant
  if (component.deriveFrom) lines.push(`- Variant: ${GOLDEN_PROMPT_SUFFIX}`);

  // Reference images instruction
  const hasRefs = component.referenceImages.length > 0 || request.sessionReferenceImages.length > 0;
  if (hasRefs) {
    lines.push('- Use the provided reference images as a guide for shape, composition, and layout only. Do not replicate their exact colors or textures — adapt the design to match the style specified above.');
  }

  const systemContext = lines.join('\n');
  const fullPrompt = `${systemContext}\n\nGenerate: ${prompt}`;

  return { systemContext, userPrompt: prompt, fullPrompt };
}

function resolveStyleText(style: string): string {
  if (style === 'coc') return 'Clash of Clans cartoon, vibrant colors, bold outlines, 3D-rendered cartoon, Supercell art style';
  if (style === 'immortal-legend') return 'epic dark fantasy, dramatic lighting, mythological atmosphere, ornate gold details, cinematic dark palette';
  if (style === 'custom') {
    return useSlotAssetGeneratorStore.getState().imageSettings.customStyleText || '';
  }
  return style ? `${style}` : '';
}

function resolveQualityText(quality: string): string {
  if (quality === '4K') return 'ultra high quality, highly detailed, 4K resolution';
  if (quality === '2K') return 'high quality, detailed, 2K resolution';
  if (quality === '1K') return 'high quality';
  return 'standard quality';
}

/**
 * Convert a SlotAssetGenerationRequest to ImageGenerationSettings
 * for the existing image generator service.
 */
async function toImageSettings(request: SlotAssetGenerationRequest): Promise<ImageGenerationSettings> {
  const store = useSlotAssetGeneratorStore.getState();

  // Merge component + session reference images and resolve paths to blob URLs
  const allRefs = [
    ...request.component.referenceImages,
    ...request.sessionReferenceImages,
  ];
  const resolvedUrls = await Promise.all(allRefs.map(async (ref) => {
    try { return await resolveRefToBlobUrl(ref); } catch { return null; }
  }));
  const referenceImages: ReferenceImage[] = resolvedUrls
    .filter((url): url is string => url !== null)
    .map((url, i) => ({
      id: `ref-${i}`,
      url,
      fileName: `reference-${i + 1}.png`,
      source: 'upload' as const,
    }));

  // Build structured prompt
  const structured = buildStructuredSlotPrompt(request);

  // For text-only providers, append reference filenames
  // (Gemini sends images as inlineData, doesn't need this)
  let effectivePrompt = structured.fullPrompt;
  const isGemini = request.model === 'soul-2.0' || request.model === 'nano-banana-2';
  if (!isGemini && referenceImages.length > 0) {
    const refNames = referenceImages.map(r => r.fileName).join(', ');
    effectivePrompt += `\nReference style from: ${refNames}`;
  }

  return {
    prompt: request.prompt,            // Original user prompt (for metadata)
    effectivePrompt,                   // Full structured prompt (bypasses buildEffectivePrompt)
    width: request.component.width ?? 512,
    height: request.component.height ?? 512,
    model: request.model,
    style: request.style,
    qualityPreset: request.quality,
    customStyleText: store.imageSettings.customStyleText,
    batchCount: 1,
    aspectRatio: '1:1' as const,
    seedMode: 'auto' as const,
    styleStrength: 0.5,
    removeBackground: request.component.format === 'png_transparent',
    referenceImages,
  };
}

/**
 * Generate slot asset images with streaming results via AsyncGenerator.
 * Yields each result as it arrives, enabling progressive UI updates.
 */
export async function* generateSlotAsset(
  request: SlotAssetGenerationRequest,
  signal?: AbortSignal
): AsyncGenerator<{ index: number; result: SlotAssetResult }> {
  const settings = await toImageSettings(request);
  const total = request.imageCount;

  for (let i = 0; i < total; i++) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    // Stagger between requests
    if (i > 0) {
      await delay(STAGGER_MS, signal);
    }

    try {
      const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
      const results = await generateImages({
        settings: { ...settings, batchCount: 1 },
        apiKeys,
        signal,
      });

      const genResult = results[0];
      if (!genResult || genResult.status === 'failed') {
        yield {
          index: i,
          result: {
            id: generateId(),
            imageUrl: '',
            status: 'error',
            error: genResult?.error ?? 'Generation failed',
          },
        };
        continue;
      }

      yield {
        index: i,
        result: {
          id: generateId(),
          imageUrl: genResult.url,
          status: 'ready',
        },
      };
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      yield {
        index: i,
        result: {
          id: generateId(),
          imageUrl: '',
          status: 'error',
          error: err instanceof Error ? err.message : 'Generation failed',
        },
      };
    }
  }
}

/**
 * Re-roll a single image with the same request parameters.
 */
export async function rerollSingleImage(
  request: SlotAssetGenerationRequest,
  signal?: AbortSignal
): Promise<SlotAssetResult> {
  const settings = await toImageSettings(request);

  try {
    const apiKeys = useSlotAssetGeneratorStore.getState().apiKeys;
    const results = await generateImages({
      settings: { ...settings, batchCount: 1 },
      apiKeys,
      signal,
    });

    const genResult = results[0];
    if (!genResult || genResult.status === 'failed') {
      return {
        id: generateId(),
        imageUrl: '',
        status: 'error',
        error: genResult?.error ?? 'Re-roll failed',
      };
    }

    return {
      id: generateId(),
      imageUrl: genResult.url,
      status: 'ready',
    };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    return {
      id: generateId(),
      imageUrl: '',
      status: 'error',
      error: err instanceof Error ? err.message : 'Re-roll failed',
    };
  }
}

/**
 * Save a generated result image to the project filesystem.
 * Creates directory structure and auto-increments filenames.
 */
export async function saveResultToProject(
  result: SlotAssetResult,
  component: SlotComponentDef,
): Promise<string> {
  if (!hasProjectRoot() || !result.imageUrl) {
    throw new Error('No project root or no image URL');
  }

  // Determine directory path
  const baseDir = 'assets/ai-generated/slot-components';
  let dir: string;
  if (component.group === 'symbols' && component.symbolId) {
    dir = `${baseDir}/symbols/${component.id}`;
  } else {
    dir = `${baseDir}/${component.group}`;
  }

  // Create directory
  await createDirectory(dir, true);

  // Sanitize component name for filename
  const safeName = component.name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 30)
    .replace(/-+$/, '');

  // Determine extension
  const ext = component.format === 'jpg' ? 'jpg' : 'png';

  // Find next available number
  let existingFileNames: string[] = [];
  try {
    const entries = await listDirectory(dir);
    existingFileNames = entries
      .filter((e) => e.kind === 'file')
      .map((e) => e.name);
  } catch {
    // Directory may be empty or not exist yet
  }

  const pattern = new RegExp(`^${safeName}-(\\d{3})\\.${ext}$`);
  let maxNum = 0;
  for (const file of existingFileNames) {
    const match = file.match(pattern);
    if (match) {
      maxNum = Math.max(maxNum, parseInt(match[1], 10));
    }
  }
  const nextNum = String(maxNum + 1).padStart(3, '0');
  const filename = `${safeName}-${nextNum}.${ext}`;
  const filePath = `${dir}/${filename}`;

  // Fetch blob and write
  const blob = await fetchBlob(result.imageUrl);
  const buffer = await blob.arrayBuffer();
  await writeFileAsArrayBuffer(filePath, buffer);

  return filePath;
}

/**
 * Fetch a blob from a blob: URL using XHR (bypasses CSP connect-src).
 */
async function fetchBlob(blobUrl: string): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', blobUrl, true);
    xhr.responseType = 'blob';
    xhr.onload = () => xhr.status === 200
      ? resolve(xhr.response as Blob)
      : reject(new Error(`XHR failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('XHR network error'));
    xhr.send();
  });
}

/**
 * Delay helper with abort support.
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}
